import { useEffect, useMemo } from "react";
import type { UseInfiniteQueryResult } from "react-query";
import type { PaginatedResultsResponse } from "../types/generic.types";

/**
 * This/These hooks are not specifically for any service, rather
 * these are hooks that work along with existing hooks to provide
 * additional functionality
 */

/**
 * useGetAllPages fetches all pages of an infinite query in a way, such that
 * it appears as a regular `useQuery`, where all data is expected to return from
 * the query at once.
 * @param queryResult
 * @returns
 */
export const useGetAllPages = <T = unknown>(
  queryResult: UseInfiniteQueryResult<PaginatedResultsResponse<T>, unknown>
) => {
  useEffect(() => {
    if (queryResult.hasNextPage && !queryResult.isFetching)
      queryResult.fetchNextPage();
  }, [queryResult]);

  const isLoading = !queryResult.isFetched || !!queryResult.hasNextPage;

  const data = useMemo(
    () => queryResult.data?.pages?.flatMap(i => i.results),
    [queryResult.data?.pages]
  );

  return {
    ...queryResult,
    isLoading,
    data: isLoading ? [] : data,
    status: isLoading ? "loading" : queryResult.status
  };
};
